/**
 * KeyValueTable — a JSONB blob (hyperparams / metrics) rendered verbatim
 * as a two-column table on the run detail page.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface KeyValueTableProps {
  title: string
  data: Record<string, unknown>
  /** Optional row rendered first and visually highlighted (the headline score). */
  highlight?: { key: string; value: string }
}

function renderValue(value: unknown): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

export function KeyValueTable({ title, data, highlight }: KeyValueTableProps): React.JSX.Element {
  const entries = Object.entries(data)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && !highlight ? (
          <p className="text-sm text-muted-foreground">None reported.</p>
        ) : (
          <dl className="divide-y divide-border">
            {highlight && (
              <div className="flex items-center justify-between gap-4 bg-primary/5 px-1 py-2">
                <dt className="font-mono text-xs font-medium text-foreground">{highlight.key}</dt>
                <dd className="text-sm font-medium tabular-nums text-foreground">
                  {highlight.value}
                </dd>
              </div>
            )}
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between gap-4 px-1 py-2">
                <dt className="font-mono text-xs text-muted-foreground">{key}</dt>
                <dd className="break-all text-right text-sm tabular-nums text-foreground">
                  {renderValue(value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  )
}
